import axios from "axios";
import { isValidUriString } from "functions/isValidUriString";
import { Operation } from "rfc6902";
import { IAxiosReturn } from "../interface";
import { processAxiosError } from "../processAxiosError";
import { TlfItemReadDto } from "./TlfItemReadDto";
import { tlfPreProcess } from "./tlfPreProcess";

export interface PatchTlfItemDetails<K extends keyof TlfItemReadDto> {
  id: string;
  azsolId: string;
  version?: number;
  key: K;
  newValue: TlfItemReadDto[K];
}

export type ITlfItemReturn = IAxiosReturn<TlfItemReadDto>;

/**
 * Apply a JSON patch to a TLF item and return the updated item.
 *
 * @param backEndUrl - Base backend URL
 * @param patchDetails - Object describing the patch
 * @param controller - Optional AbortController for cancelling the request
 * @returns Updated TLF item wrapped in ITlfItemReturn
 */
export const patchTlfItem = async <K extends keyof TlfItemReadDto>(
  backEndUrl: string,
  patchDetails: PatchTlfItemDetails<K>,
  controller: AbortController,
): Promise<ITlfItemReturn> => {
  try {
    const patch: Operation = {
      op: "replace",
      path: `/${patchDetails.key}`,
      value: patchDetails.newValue,
    };
    let url = `${backEndUrl}TlfItems/${patchDetails.id}`;

    if (patchDetails.azsolId && isValidUriString(patchDetails.azsolId))
      url += `/${patchDetails.azsolId}`;

    const response = await axios.patch(url, [patch], {
      headers: {
        "Content-Type": "application/json-patch+json",
      },
      signal: controller.signal,
    });
    return {
      success: true,
      data: tlfPreProcess(response.data),
    };
  } catch (error) {
    return processAxiosError("Patch TLF item failed", error);
  }
};
